import { createBatchedParamsArrayFromIds, creatorbase } from '@round/api';
import uniq from 'lodash/uniq';
import {
    useAggregatedPostStatsQueries,
    useBrandsQueriesByIds,
    useCampaigns as useCampaignsQuery,
    useProjectsListQueries,
    useReports,
    useTeamsListQueries,
} from '@round/creatorbase-helpers/api-hooks';
import usePublicReportId from 'Modules/Plans/PublicReports/usePublicReportId';

type Platform = 'instagram' | 'tiktok' | 'youtube';

export default function useCampaigns(platform: Platform) {
    const publicReportId = usePublicReportId();

    const campaignsQuery = useCampaignsQuery(
        {
            platform,
            public_report_id: publicReportId ?? undefined,
            page_size: 1000,
            ordering: 'public-report-platform-order',
        },
        { enabled: !!publicReportId }
    );

    const campaigns = campaignsQuery.data?.results ?? [];
    const campaignIds = campaigns.map((c) => c.id);

    const campaignStatsData = useAggregatedPostStatsQueries(
        campaignIds.map((campaignId) => ({
            params: { campaign_id: campaignId, publicReportId: publicReportId ?? undefined },
            config: { enabled: !!publicReportId },
        })),
        {
            combine: (results) =>
                campaignIds.reduce<
                    Record<
                        number,
                        { data: (typeof results)[number]['data'] | null; isFetching: boolean; isError: boolean }
                    >
                >((acc, campaignId, index) => {
                    const query = results[index];
                    acc[campaignId] = {
                        data: query?.data ?? null,
                        isFetching: query?.isFetching ?? false,
                        isError: query?.isError ?? false,
                    };
                    return acc;
                }, {}),
        }
    );

    const reportsQuery = useReports({ public_report_id: publicReportId ?? undefined }, { enabled: !!publicReportId });
    const projectIds = reportsQuery.data?.results[0]?.project_ids ?? [];
    const hasProjects = projectIds.length > 0;

    const { projectsData, isProjectsLoading } = useProjectsListQueries(
        createBatchedParamsArrayFromIds(projectIds, (ids) => ({ id: ids.toString() })).map((params) => ({
            params: { ...params, publicReportId: publicReportId ?? undefined },
            config: { enabled: hasProjects },
        })),
        {
            combine: (results) => ({
                projectsData: new Map<number, creatorbase.PublicProject>(
                    results.flatMap((result) => result.data?.results ?? []).map((project) => [project.id, project])
                ),
                isProjectsLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    const brandIds = uniq(
        [...projectsData.values()]
            .map((project) => project.brand_id)
            .filter((id): id is number => typeof id === 'number')
    );

    const brandsData = useBrandsQueriesByIds([brandIds], {
        publicReportId: publicReportId ?? undefined,
        combine: (queries) => {
            const query = queries[0];
            const status: 'loading' | 'success' | 'error' =
                !query || query.isPending ? 'loading' : query.isError ? 'error' : 'success';
            const dict: Record<
                number,
                { data: creatorbase.PublicBrand | null; status: typeof status; error: string | null }
            > = {};
            for (const id of brandIds) {
                const raw = query?.data?.results.find((b) => b.id === id) ?? null;
                const brand = raw && creatorbase.isPublicBrand(raw) ? raw : null;
                dict[id] = { data: brand, status, error: query?.isError ? 'Could not load brands' : null };
            }
            return dict;
        },
    });

    const teamIds = uniq(
        [...projectsData.values()]
            .map((project) => project.team_id)
            .filter((id): id is number => typeof id === 'number')
    );
    const hasTeams = teamIds.length > 0;

    const { teamsData, isTeamsLoading } = useTeamsListQueries(
        createBatchedParamsArrayFromIds(teamIds, (ids) => ({ id: ids.toString() })).map((params) => ({
            params: { ...params, publicReportId: publicReportId ?? undefined },
            config: { enabled: hasTeams },
        })),
        {
            combine: (results) => ({
                teamsData: new Map<number, creatorbase.PublicTeam>(
                    results.flatMap((result) => result.data?.results ?? []).map((team) => [team.id, team])
                ),
                isTeamsLoading: results.some((result) => result.isLoading),
            }),
        }
    );

    return {
        campaigns,
        isCampaignsLoading: campaignsQuery.isLoading,
        isCampaignsError: campaignsQuery.isError,
        isCampaignsSuccess: campaignsQuery.isSuccess,
        campaignStatsData,
        projectsData,
        brandsData,
        teamsData,
        isTeamsLoading,
        isProjectsLoading,
        isBrandLoading: (campaignId: number) => {
            const projectId = campaigns.find((c) => c.id === campaignId)?.project_id;
            const project = typeof projectId === 'number' ? projectsData.get(projectId) ?? null : null;
            return Boolean(project && brandsData[project.brand_id]?.status === 'loading');
        },
        isCampaignStatsLoading: (campaignId: number) => campaignStatsData[campaignId]?.isFetching ?? false,
    };
}
